/**
 * The table columns for each MF record list.
 *
 * Field names follow the user platform's record types — the lists return the
 * same records its portfolio reads per investor:
 *
 *   lumpsum orders, SIPs   interfaces/IMutualFundDetails.ts  MFOrderHistoryResponse
 *   redemptions, switches  helpers/mfTransactions.ts         MF{Redemption,Switch}HistoryItem
 *   SWPs, STPs             helpers/mf{Redemption,Switch}Plans.ts
 *   SIP modifications      helpers/mfPlanModification.ts     PlanModificationInstruction
 *
 * Anything a record does not carry renders as "—" rather than failing, and
 * every field is still one click away in the record's details view.
 */

import React from "react";
import type { MFRecord } from "../../../Api/mfAdminInvestments";
import { MF_MANDATE_CHANGE_LIMIT } from "../../../Api/mfAdminPlanModification";
import { formatMFInstallmentDay, normalizeMFFrequency } from "../../../Helpers/mfSchedule";
import type { MFListKind } from "../../../Api/mfAdminOverview";
import {
  createdAtOf,
  formatDate,
  formatMoneyExact,
  formatUnits,
  humanizeKey,
  toNumber,
} from "./mfOverviewFormat";
import { mfStatusLines, mfStatusTone } from "../../../Helpers/mfOrderStatus";
import type { DirectoryInvestor, InvestorDirectory } from "./useOverviewLookups";
import styles from "./mf-overview.module.css";

export interface ColumnContext {
  schemeNames: Record<string, string>;
  directory: InvestorDirectory;
  /** The investor a row belongs to — its own `user`, or found through its SIP. */
  investorUuidFor: (row: MFRecord) => string;
  /** SIP records by purchase plan id, for rows that name only their SIP. */
  plans: Record<string, MFRecord>;
  /** Present only when this admin may open an investor's MF dashboard. */
  onOpenInvestor?: (investor: DirectoryInvestor) => void;
}

export type SortValue = string | number | null;

export interface OverviewColumn {
  key: string;
  label: string;
  numeric?: boolean;
  render: (row: MFRecord, ctx: ColumnContext) => React.ReactNode;
  /** What the column sorts by; a column without one cannot be sorted. Null sorts last. */
  sortValue?: (row: MFRecord, ctx: ColumnContext) => SortValue;
}

/** A date's timestamp, for sorting; null when there is none. */
const timeOf = (value?: string | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const Stacked = ({ primary, meta }: { primary: React.ReactNode; meta?: React.ReactNode }) => (
  <>
    <div className={styles.primaryText}>{primary}</div>
    {meta ? <div className={styles.metaText}>{meta}</div> : null}
  </>
);

const schemeLabel = (ctx: ColumnContext, isin?: string | null, name?: string | null) =>
  name || (isin ? ctx.schemeNames[isin] || isin : "—");

/** The scheme label to sort on — null rather than "—" when there is none. */
const schemeSortValue = (ctx: ColumnContext, isin?: string | null, name?: string | null) =>
  name || isin ? schemeLabel(ctx, isin, name) : null;

const folioOr = (row: MFRecord, fallback?: string | null) =>
  row.folio_number ? `Folio ${row.folio_number}` : fallback || undefined;

/** An amount, or the units when the transaction was placed in units. */
const amountOrUnits = (amount: unknown, units: unknown) =>
  toNumber(amount) !== null && toNumber(amount) !== 0
    ? formatMoneyExact(amount)
    : toNumber(units) !== null
    ? `${formatUnits(units)} units`
    : "—";

/** FP's frequency codes, as the SWP/STP and SIP dialogs name them. */
const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  day_in_a_week: "Weekly",
  day_in_a_fortnight: "Fortnightly",
  twice_a_month: "Twice a month",
  four_times_a_month: "Four times a month",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
};

/**
 * "Weekly · Wednesday", "Monthly · Day 22", "Daily". The day is a weekday for
 * weekly and fortnightly plans and a date otherwise (Helpers/mfSchedule).
 */
const schedule = (row: MFRecord) => {
  if (!row.frequency) return "—";
  const frequency = normalizeMFFrequency(String(row.frequency));
  const label = FREQUENCY_LABELS[frequency] || humanizeKey(frequency);
  const day = toNumber(row.installment_day);
  return day === null ? label : `${label} · ${formatMFInstallmentDay(frequency, day)}`;
};

const installmentsLeft = (row: MFRecord) => {
  const remaining = toNumber(row.remaining_installments);
  const total = toNumber(row.number_of_installments);
  if (remaining !== null && total !== null) return `${remaining} of ${total} left`;
  if (total !== null) return `${total} instalments`;
  return undefined;
};

const failureOf = (row: MFRecord): string =>
  String(row.failure_reason || row.failed_reason || row.failure_code || row.reason || "").trim();

/* ─────────────────────────── shared columns ─────────────────────────── */

const investorColumn: OverviewColumn = {
  key: "investor",
  label: "Investor",
  sortValue: (row, ctx) => {
    const uuid = ctx.investorUuidFor(row);
    const investor = uuid ? ctx.directory.byUuid[uuid] : undefined;
    return investor ? investor.name : uuid || null;
  },
  render: (row, ctx) => {
    const uuid = ctx.investorUuidFor(row);
    const investor = uuid ? ctx.directory.byUuid[uuid] : undefined;
    if (!investor) {
      return (
        <Stacked
          primary={uuid ? (ctx.directory.loading ? "Loading…" : "Unknown investor") : "—"}
          meta={uuid || undefined}
        />
      );
    }
    const meta =
      [investor.mobile && `+91 ${investor.mobile}`, investor.smestId].filter(Boolean).join(" · ") ||
      investor.email;
    if (!ctx.onOpenInvestor) return <Stacked primary={investor.name} meta={meta} />;
    const open = ctx.onOpenInvestor;
    return (
      <button
        type="button"
        className={styles.investorLink}
        title="Open this investor's MF dashboard in a new tab"
        onClick={(event) => {
          // The row opens the record's details; this opens the investor.
          event.stopPropagation();
          open(investor);
        }}
      >
        <Stacked primary={investor.name} meta={meta} />
      </button>
    );
  },
};

const TONE_CLASS: Record<string, string> = {
  success: styles.statusSuccess,
  pending: styles.statusPending,
  failed: styles.statusFailed,
};

/**
 * Both statuses a record carries, exactly as the API returned them and each
 * labelled — "Webhook" and "State" (Helpers/mfOrderStatus). Sorted by the
 * webhook status, or the state where there is none.
 */
const statusColumn: OverviewColumn = {
  key: "status",
  label: "Status",
  sortValue: (row) =>
    String(row.webhook_status || row.state || row.status || "").toLowerCase() || null,
  render: (row) => {
    const lines = mfStatusLines(row);
    const failed = lines.some((line) => line.value && mfStatusTone(line.value) === "failed");
    const reason = failed ? failureOf(row) : "";
    return (
      <div className={styles.statusStack}>
        {lines.length
          ? lines.map((line) => (
              <div key={line.label} className={styles.statusLine}>
                <span className={styles.statusLabel}>{line.label}</span>
                {line.value ? (
                  <span className={`${styles.status} ${TONE_CLASS[mfStatusTone(line.value)]}`}>
                    {line.value}
                  </span>
                ) : (
                  <span className={styles.detailEmpty}>—</span>
                )}
              </div>
            ))
          : "—"}
        {row.cancellation_scheduled_on ? (
          <div className={styles.metaText}>
            Cancellation scheduled · {formatDate(row.cancellation_scheduled_on)}
          </div>
        ) : null}
        {reason ? <div className={styles.metaText}>{reason}</div> : null}
      </div>
    );
  },
};

const createdColumn: OverviewColumn = {
  key: "created",
  label: "Created",
  sortValue: (row) => timeOf(createdAtOf(row)),
  render: (row) => formatDate(createdAtOf(row)),
};

/** An amount column's sort: by the rupee amount, whatever the cell displays. */
const byAmount = (row: MFRecord): SortValue => toNumber(row.amount);

const schemeColumn = (idField: string): OverviewColumn => ({
  key: "scheme",
  label: "Scheme",
  sortValue: (row, ctx) => schemeSortValue(ctx, row.scheme || row.isin, row.scheme_name || row.fund_name),
  render: (row, ctx) => (
    <Stacked
      primary={schemeLabel(ctx, row.scheme || row.isin, row.scheme_name || row.fund_name)}
      meta={folioOr(row, row[idField])}
    />
  ),
});

const switchSchemesColumn = (idField: string): OverviewColumn => ({
  key: "schemes",
  label: "From → To",
  sortValue: (row, ctx) => schemeSortValue(ctx, row.switch_out_scheme),
  render: (row, ctx) => (
    <Stacked
      primary={
        <>
          {schemeLabel(ctx, row.switch_out_scheme)}
          <span className={styles.arrow}> → </span>
          {schemeLabel(ctx, row.switch_in_scheme)}
        </>
      }
      meta={folioOr(row, row[idField])}
    />
  ),
});

const scheduleColumn: OverviewColumn = {
  key: "schedule",
  label: "Schedule",
  render: (row) => <Stacked primary={schedule(row)} meta={installmentsLeft(row)} />,
};

/**
 * The SIP's e-mandate, and how many of its five replacements are spent. A SIP
 * at five cannot take a higher instalment — that needs a new mandate.
 * `mandate_details` and `mandate_update_count` are on the live SIP list.
 */
const mandateColumn: OverviewColumn = {
  key: "mandate",
  label: "Mandate",
  // Sorted by changes spent, so the SIPs out of changes are one click away.
  sortValue: (row) => toNumber(row.mandate_update_count),
  render: (row) => {
    const details = row.mandate_details || {};
    const status = details.mandate_status ? humanizeKey(String(details.mandate_status).toLowerCase()) : "—";
    const used = toNumber(row.mandate_update_count);
    return (
      <Stacked
        primary={status}
        meta={
          used === null ? undefined : (
            <span className={used >= MF_MANDATE_CHANGE_LIMIT ? styles.metaAlert : undefined}>
              {`${used}/${MF_MANDATE_CHANGE_LIMIT} changes used`}
            </span>
          )
        }
      />
    );
  },
};

/**
 * A withdrawal or transfer plan's instalment, and what it was before its last
 * amount change — `update_metadata` on the live SWP and STP lists.
 */
const planInstalmentColumn: OverviewColumn = {
  key: "amount",
  label: "Instalment",
  numeric: true,
  sortValue: byAmount,
  render: (row) => {
    const history = Array.isArray(row.update_metadata) ? row.update_metadata : [];
    const latest = history[history.length - 1];
    return (
      <Stacked
        primary={formatMoneyExact(row.amount)}
        meta={
          latest && toNumber(latest.old_amount) !== null
            ? `Changed from ${formatMoneyExact(latest.old_amount)}`
            : undefined
        }
      />
    );
  },
};

const nextInstallmentColumn: OverviewColumn = {
  key: "next",
  label: "Next instalment",
  sortValue: (row) => timeOf(row.next_installment_date),
  render: (row) => formatDate(row.next_installment_date),
};

/* ───────────────────────────── per record ───────────────────────────── */

export const OVERVIEW_COLUMNS: Record<MFListKind, OverviewColumn[]> = {
  purchases: [
    investorColumn,
    {
      key: "scheme",
      label: "Scheme",
      sortValue: (row, ctx) => schemeSortValue(ctx, row.scheme || row.isin, row.scheme_name || row.fund_name),
      render: (row, ctx) => (
        <Stacked
          primary={schemeLabel(ctx, row.scheme || row.isin, row.scheme_name || row.fund_name)}
          meta={
            // `plan` names the SIP a purchase is an instalment of; it is null on
            // a one-time order (every row in the live list, 24-09-2026).
            [row.plan ? "SIP instalment" : "", folioOr(row, row.purchase_id)]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        />
      ),
    },
    {
      key: "amount",
      label: "Amount",
      numeric: true,
      sortValue: byAmount,
      render: (row) => {
        const units = row.allotted_units != null ? row.allotted_units : row.units;
        return (
          <Stacked
            primary={formatMoneyExact(row.amount)}
            meta={toNumber(units) !== null ? `${formatUnits(units)} units allotted` : undefined}
          />
        );
      },
    },
    statusColumn,
    createdColumn,
  ],
  sips: [
    investorColumn,
    schemeColumn("purchase_plan_id"),
    { key: "amount", label: "Instalment", numeric: true, sortValue: byAmount, render: (row) => formatMoneyExact(row.amount) },
    scheduleColumn,
    nextInstallmentColumn,
    mandateColumn,
    statusColumn,
    createdColumn,
  ],
  redemptions: [
    investorColumn,
    schemeColumn("redemption_id"),
    {
      key: "amount",
      label: "Amount",
      numeric: true,
      sortValue: byAmount,
      render: (row) => (
        <Stacked
          primary={amountOrUnits(row.amount, row.units)}
          meta={
            // Once processed, the units that were sold (live list: the redeemed
            // amount repeats the requested one, so it adds nothing).
            toNumber(row.redeemed_units) !== null
              ? `${formatUnits(row.redeemed_units)} units redeemed`
              : undefined
          }
        />
      ),
    },
    statusColumn,
    createdColumn,
  ],
  switches: [
    investorColumn,
    switchSchemesColumn("switch_id"),
    {
      key: "amount",
      label: "Amount",
      numeric: true,
      sortValue: byAmount,
      render: (row) => (
        <Stacked
          primary={amountOrUnits(row.amount, row.units)}
          meta={
            // The switch-in side is never filled on the live list; the
            // switch-out units are what was actually moved.
            toNumber(row.switched_out_units) !== null
              ? `${formatUnits(row.switched_out_units)} units switched out`
              : undefined
          }
        />
      ),
    },
    statusColumn,
    createdColumn,
  ],
  swps: [
    investorColumn,
    schemeColumn("redemption_plan_id"),
    planInstalmentColumn,
    scheduleColumn,
    nextInstallmentColumn,
    statusColumn,
    createdColumn,
  ],
  stps: [
    investorColumn,
    switchSchemesColumn("switch_plan_id"),
    planInstalmentColumn,
    scheduleColumn,
    nextInstallmentColumn,
    statusColumn,
    createdColumn,
  ],
  modifications: [
    investorColumn,
    {
      key: "plan",
      label: "SIP",
      // The instruction names only its SIP's id; the fund comes from the SIP.
      sortValue: (row, ctx) => {
        const sip = ctx.plans[String(row.plan || "")];
        return sip ? schemeSortValue(ctx, sip.scheme, sip.scheme_name) : row.plan || null;
      },
      render: (row, ctx) => {
        const sip = ctx.plans[String(row.plan || "")];
        return sip ? (
          <Stacked
            primary={schemeLabel(ctx, sip.scheme, sip.scheme_name)}
            meta={<span className={styles.mono}>{row.plan}</span>}
          />
        ) : (
          <Stacked primary={<span className={styles.mono}>{row.plan || "—"}</span>} meta={row.id} />
        );
      },
    },
    {
      key: "change",
      label: "Change",
      // By the new instalment amount; mandate swaps carry none and sort last.
      sortValue: (row) => toNumber((row.amount || {}).to),
      render: (row) => {
        // One modification is two instructions: an amount change, and the
        // mandate swap that funds it (helpers/mfPlanModification.ts upstream).
        const amount = row.amount || {};
        const source = row.payment_source || {};
        if (amount.from != null || amount.to != null) {
          return (
            <Stacked
              primary={`${formatMoneyExact(amount.from)} → ${formatMoneyExact(amount.to)}`}
              meta="Instalment amount"
            />
          );
        }
        if (source.from != null || source.to != null) {
          return <Stacked primary={`${source.from || "—"} → ${source.to || "—"}`} meta="Mandate" />;
        }
        return "—";
      },
    },
    statusColumn,
    {
      key: "created",
      label: "Created",
      sortValue: (row) => timeOf(createdAtOf(row)),
      render: (row) => (
        <Stacked
          primary={formatDate(createdAtOf(row))}
          meta={
            row.completed_at
              ? `Completed ${formatDate(row.completed_at)}`
              : row.failed_at
              ? `Failed ${formatDate(row.failed_at)}`
              : undefined
          }
        />
      ),
    },
  ],
};
